import React, { useEffect } from "react";
import { useLocation } from "react-router-dom";
import { Download, Gift, MessagesSquare, PlayCircle, Puzzle } from "lucide-react";
import { BLUE, Cta, NAVY, OfferTimer, ORANGE, Price, TypeFlipGrid, useOffer } from "@/components/formel/FormelParts";
import { PocketCoachDemo } from "@/components/formel/PocketCoachDemo";
import { trackFormelDownsell } from "@/lib/ga4";

export { FORMEL_CHECKOUT_URL } from "@/components/formel/FormelParts";

const INSIDE = [
  [PlayCircle, "Video-Training", "rund 70 Minuten, Modell und die vier Fokustypen verständlich erklärt"],
  [Puzzle, "Formulierungsbausteine", "für Katze, Eule, Biene und Hund – plus eine zusammengeführte Botschaft für alle vier"],
  [MessagesSquare, "Praxisbeispiele", "ein konkretes Szenario und Beispiele für Präsentationen, Meetings und E-Mails"],
  [Download, "Leitfaden & Downloads", "Kommunikationsleitfaden für deine Vorbereitung und Arbeitsmaterialien"],
];

const Hero = ({ variant, offer }) => (
  <header className="bg-[#FAF7F2] px-6 sm:px-8 pt-14 pb-16 sm:pt-24 sm:pb-24" data-testid="formel-hero">
    <div className="max-w-5xl mx-auto grid md:grid-cols-[1.2fr_0.8fr] gap-12 md:gap-16 items-center">
      <div className="text-center md:text-left">
        <h1 className="text-5xl sm:text-6xl lg:text-7xl font-extrabold tracking-tight leading-[1.0]" style={{ color: NAVY }} data-testid="formel-hero-headline">
          Nicht lauter.<br /><span style={{ color: ORANGE }}>Anders.</span>
        </h1>
        <p className="mt-6 text-lg sm:text-xl leading-relaxed text-slate-600 max-w-lg mx-auto md:mx-0" data-testid="formel-hero-subheadline">
          Warum Menschen auf dasselbe Angebot so unterschiedlich reagieren – und wie du es so aufbaust, dass mehr mitgehen.
        </p>
        <div className="mt-9 flex flex-col sm:flex-row items-center gap-4 sm:gap-6 justify-center md:justify-start">
          <Cta label="Jetzt entdecken" position="hero" variant={variant} testId="formel-cta-hero" href={offer.href} />
        </div>
        {offer.active && <div className="mt-5"><OfferTimer left={offer.left} testId="formel-timer-hero" /></div>}
      </div>
      <img src="/images/formel-cover.webp" alt="Die 4 Fokustypen Formel – Online-Videotraining" width="1000" height="994"
        className="w-full max-w-[240px] sm:max-w-xs md:max-w-sm mx-auto rounded-2xl shadow-xl" data-testid="formel-cover" />
    </div>
  </header>
);

const Mirror = ({ variant, offer }) => (
  <section className="px-5 sm:px-8 py-16 sm:py-24" data-testid="formel-mirror">
    <div className="max-w-6xl mx-auto">
      <p className="text-xs font-bold uppercase tracking-widest" style={{ color: BLUE }}>Warum kommt es nicht an?</p>
      <h2 className="mt-2 text-3xl sm:text-4xl font-extrabold leading-tight max-w-2xl" style={{ color: NAVY }}>
        Es liegt nicht an den anderen. Sie warten nur auf etwas anderes.
      </h2>
      <p className="mt-4 text-base sm:text-lg text-slate-600 max-w-2xl">Tipp auf einen Typ und sieh, worauf er wartet, bevor er mitgeht:</p>
      <div className="mt-8"><TypeFlipGrid /></div>
      <div className="mt-10 rounded-3xl bg-[#0F172A] text-white p-6 sm:p-10 flex flex-col md:flex-row md:items-center gap-6 justify-between" data-testid="formel-loop">
        <p className="text-lg sm:text-2xl font-bold leading-snug max-w-2xl">
          Wenn du weißt, worauf jeder wartet, musst du nicht lauter erklären. <span style={{ color: ORANGE }}>Nur in der richtigen Reihenfolge.</span>
        </p>
        <Cta label="Zur Formel" position="loop" variant={variant} testId="formel-cta-loop" href={offer.href} light />
      </div>
    </div>
  </section>
);

const Inside = () => (
  <section className="px-5 sm:px-8 py-16 sm:py-24 bg-[#FAF7F2]" data-testid="formel-included">
    <div className="max-w-6xl mx-auto">
      <h2 className="text-3xl sm:text-4xl font-extrabold" style={{ color: NAVY }}>Das steckt drin</h2>
      <div className="mt-8 grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {INSIDE.map(([Icon, title, text]) => (
          <div key={title} className="rounded-3xl bg-white p-6 shadow-sm border border-slate-100 transition-transform duration-200 hover:-translate-y-1" data-testid={`formel-inside-${title.split(" ")[0].toLowerCase()}`}>
            <span className="grid place-items-center w-12 h-12 rounded-2xl mb-4" style={{ backgroundColor: "#FDE7D6" }}><Icon className="w-6 h-6" style={{ color: ORANGE }} /></span>
            <p className="text-lg font-extrabold" style={{ color: NAVY }}>{title}</p>
            <p className="mt-1 text-sm sm:text-base text-slate-600 leading-snug">{text}</p>
          </div>
        ))}
      </div>
      <p className="mt-6 text-sm sm:text-base text-slate-600" data-testid="formel-uses">
        Für Team-Gespräche, Meetings, Präsentationen, Workshops, E-Mails an mehrere Menschen, Angebote und Einladungen.
      </p>
    </div>
  </section>
);

const Bonus = () => (
  <section className="px-5 sm:px-8 py-16 sm:py-24" data-testid="formel-bonus">
    <div className="max-w-5xl mx-auto grid md:grid-cols-[1.1fr_0.9fr] gap-10 items-center">
      <div>
        <div className="flex items-center gap-3">
          <img src="/images/andreas-portrait.jpg" alt="Andreas" className="w-14 h-14 rounded-full object-cover shadow-md ring-4 ring-[#DCE7F7]" data-testid="formel-portrait" />
          <p className="inline-flex items-center gap-2 text-xs font-bold uppercase tracking-widest" style={{ color: ORANGE }}><Gift className="w-4 h-4" /> Persönlicher Bonus</p>
        </div>
        <h2 className="mt-4 text-2xl sm:text-3xl font-extrabold" style={{ color: NAVY }} data-testid="formel-bonus-headline">Dein PocketCoach: die Formel für deine eigenen Gespräche</h2>
        <p className="mt-3 text-base sm:text-lg text-slate-700 leading-relaxed" data-testid="formel-bonus-text">
          Der PocketCoach ist ein interaktives Coaching-Tool. Du beschreibst eine Situation, er zeigt dir, wie dein Gegenüber vermutlich tickt, und schlägt dir einen passenden ersten Satz vor. Auf Handy und Rechner, wann immer du ihn brauchst.
        </p>
        <p className="mt-3 text-sm sm:text-base text-slate-500 leading-relaxed" data-testid="formel-bonus-zugang">
          Nach deiner Buchung bekommst du von mir eine E-Mail mit deinem Zugang.
        </p>
      </div>
      <PocketCoachDemo />
    </div>
  </section>
);

const Offer = ({ variant, offer }) => (
  <section className="px-5 sm:px-8 py-16 sm:py-24 bg-[#0F172A] text-white" data-testid="formel-offer">
    <div className="max-w-4xl mx-auto grid md:grid-cols-[0.8fr_1.2fr] gap-10 items-center">
      <img src="/images/formel-cover.webp" alt="" className="w-full max-w-xs mx-auto rounded-3xl shadow-2xl rotate-[2deg]" />
      <div>
        <h2 className="text-3xl sm:text-4xl font-extrabold">Die 4 Fokustypen Formel</h2>
        <div className="mt-4"><Price testId="formel-price-offer" active={offer.active} dark /></div>
        <p className="mt-3 text-sm sm:text-base text-white/70" data-testid="formel-offer-note">
          {offer.active
            ? "Einmaliger Kauf des bestehenden Videotrainings. Der persönliche PocketCoach-Bonus wird nach der Buchung per E-Mail übermittelt."
            : "Einmaliger Kauf des bestehenden Videotrainings."}
        </p>
        {offer.active && <div className="mt-4"><OfferTimer left={offer.left} testId="formel-timer-offer" dark /></div>}
        <div className="mt-7"><Cta label={offer.active ? "Jetzt für 99,00 € ansehen" : "Jetzt für 179,00 € ansehen"} position="offer" variant={variant} testId="formel-cta-offer" href={offer.href} /></div>
        <p className="mt-6 text-base text-white/80" data-testid="formel-closing">Vielleicht sind die anderen gar nicht schwierig. Vielleicht haben sie nur auf ihren Punkt gewartet.</p>
      </div>
    </div>
  </section>
);

const DOWNSELL_URL = "https://inside.effektiv-kommunizieren.de/s/AJUNG/landit-pro-solo-lizenz-835e1e13/payment?displayed_plans_id=637873&plan_id=637873";

const Downsell = ({ variant, offer }) => (
  <section className="px-5 sm:px-8 py-10 bg-white text-center" data-testid="formel-downsell">
    <p className="text-sm text-slate-500">Die Formel ist gerade nichts für dich, aber der PocketCoach interessiert dich?</p>
    <a href={DOWNSELL_URL} data-testid="formel-downsell-link"
      onClick={() => trackFormelDownsell(variant, offer.active ? "intro" : "regular")}
      className="mt-1 inline-block text-sm font-semibold underline underline-offset-4 decoration-slate-300 hover:decoration-current transition-colors" style={{ color: BLUE }}>
      PocketCoach 7 Tage für 1 € testen →
    </a>
    <p className="mt-1 text-xs text-slate-400">Danach 39 € im Monat, monatlich kündbar.</p>
  </section>
);

export default function FormelLandingPage() {
  const { pathname } = useLocation();
  const variant = pathname.includes("special") ? "special" : "standard";
  const offer = useOffer();
  useEffect(() => { document.title = "Die 4 Fokustypen Formel"; }, []);
  return (
    <div className="min-h-screen bg-white" data-testid="formel-landing-page" data-variant={variant}>
      <Hero variant={variant} offer={offer} />
      <Mirror variant={variant} offer={offer} />
      <Inside />
      {offer.active && <Bonus />}
      <Offer variant={variant} offer={offer} />
      <Downsell variant={variant} offer={offer} />
    </div>
  );
}
